/**
 * GlobalView — SED Earthquake Client (Swiss Seismological Service)
 *
 * Fetches earthquake data from the Swiss Seismological Service (SED)
 * at ETH Zürich via FDSN Web Services (fdsnws-event).
 *
 * Endpoint: https://eida.ethz.ch/fdsnws/event/1/query
 * Format: pipe-separated text (no API key required)
 *
 * Maps parsed rows → Entity[] for the entity store.
 * Swiss earthquakes use type 'earthquake' + source 'sed'.
 */

import type { Entity } from '@globalview/shared';
import { magnitudeToColor, magnitudeToSize } from './usgsEarthquakeClient';

// ─── Config ──────────────────────────────────────────────────────

/** FDSN event endpoint at SED/ETH */
const SED_BASE = 'https://eida.ethz.ch/fdsnws/event/1/query';

/** How many days of data to fetch (rolling window) */
const LOOKBACK_DAYS = 30;

/** Minimum magnitude to request */
const MIN_MAGNITUDE = 0.5;

/** Scale factor for Swiss earthquakes (smaller than global) */
const SWISS_SIZE_FACTOR = 0.5;

// ─── Fetch ───────────────────────────────────────────────────────

/**
 * Fetch recent Swiss earthquakes from SED FDSN Web Service.
 */
export async function fetchSwissEarthquakes(): Promise<Entity[]> {
  const now = new Date();
  const start = new Date(now.getTime() - LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
  const startStr = start.toISOString().split('.')[0]; // remove ms
  const endStr = now.toISOString().split('.')[0];

  const params = new URLSearchParams({
    starttime: startStr,
    endtime: endStr,
    minmagnitude: String(MIN_MAGNITUDE),
    format: 'text',
    nodata: '404',
  });

  const url = `${SED_BASE}?${params.toString()}`;
  console.log(`[SED] Fetching Swiss earthquakes: ${url}`);

  try {
    const res = await fetch(url);

    if (res.status === 404) {
      // FDSN returns 404 when no data matches query
      console.warn('[SED] No earthquakes found (404)');
      return [];
    }

    if (!res.ok) {
      console.error(`[SED] HTTP ${res.status} for ${url}`);
      return [];
    }

    const text = await res.text();
    const entities = parseTextResponse(text);

    console.log(`[SED] Loaded ${entities.length} Swiss earthquakes`);
    return entities;
  } catch (err) {
    console.error('[SED] Fetch failed:', err);
    return [];
  }
}

// ─── Text Parser ─────────────────────────────────────────────────

/**
 * FDSN text format is pipe-delimited:
 * EventID|Time|Latitude|Longitude|Depth/km|Author|Catalog|Contributor|ContribID|MagType|Magnitude|MagAuthor|EventLocationName
 *
 * First line is the header — skip it.
 */
export function parseTextResponse(text: string): Entity[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return []; // header only or empty

  const entities: Entity[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const entity = parseLine(line);
    if (entity) entities.push(entity);
  }

  return entities;
}

/**
 * Parse a single FDSN text row into an Entity.
 */
function parseLine(line: string): Entity | null {
  const parts = line.split('|');
  if (parts.length < 13) return null;

  const eventId = parts[0].trim();
  const timeStr = parts[1].trim();
  const lat = parseFloat(parts[2]);
  const lon = parseFloat(parts[3]);
  const depthKm = parseFloat(parts[4]);
  const magType = parts[9].trim();
  const magnitude = parseFloat(parts[10]);
  const place = parts[12].trim();

  // Validate required fields
  if (!eventId || isNaN(lat) || isNaN(lon)) return null;
  const mag = isNaN(magnitude) ? 0 : magnitude;
  const depth = isNaN(depthKm) ? undefined : depthKm;

  return {
    id: `sed-${eventId}`,
    type: 'earthquake',
    source: 'sed',
    position: {
      lat,
      lon,
      alt: 0,
    },
    heading: undefined,
    timestamp: new Date(timeStr).toISOString(),
    style: {
      color: magnitudeToColor(mag),
      size: magnitudeToSize(mag) * SWISS_SIZE_FACTOR,
    },
    properties: {
      magnitude: mag,
      magType: magType || 'unknown',
      place: place || 'Schweiz',
      depth,
      tsunami: 0,
      alert: null,
      significance: undefined,
      url: undefined,
      felt: undefined,
      mmi: undefined,
      status: 'reviewed', // SED only returns reviewed events
    },
    confidence: 1.0, // SED events are manually reviewed
  };
}
